import { AlertCircle, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@bliks-io/ui';

interface ChatErrorBannerProps {
  error: Error | undefined;
  onRetry?: () => void;
  className?: string;
}

/**
 * Error banner displayed above the chat input when a streaming error occurs.
 * Shows the error message with an optional retry button.
 */
export function ChatErrorBanner({ error, onRetry, className }: ChatErrorBannerProps) {
  const { t } = useTranslation();

  if (!error) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-3 border-t border-destructive/20 bg-destructive/5 px-4 py-3',
        className
      )}
    >
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
      <p className="flex-1 text-sm text-destructive">{t('common.chatError')}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5',
            'text-xs font-medium text-destructive',
            'border border-destructive/20 bg-white',
            'hover:bg-destructive/5 transition-colors'
          )}
        >
          <RotateCcw className="h-3 w-3" />
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}
